import { useSetAtom } from 'jotai';
import { useCallback, useState } from 'react';
import { FileRejection, useDropzone } from 'react-dropzone';

import { useFeatureFlag } from '@/src/application/hooks/util/useFeatureFlag';
import { useHtmlEditorService } from '@/src/infrastructure/Protocol/HtmlEditor/useHtmlEditorService';
import { dropConfig } from '@/src/modules/CampaignsModule/components/HtmlUpload/config/dropZone.config';

import { useAlertMessages } from './useAlertMessages';
import { useHtml } from './useHtml';
import { useSaveHtml } from './useSaveHtml';

import {
  isLoadingUploadAtom,
  isUploadFilePageModalOpenAtom,
} from '@/modules/CampaignsModule/atoms/htmlEditorAtom';
import { useModalValidate } from '@/modules/CampaignsModule/components/HtmlUpload/hooks/useModalValidate';
import { useHtmlEditorLogger } from '@/modules/CampaignsModule/hooks/useHtmlEditorLogger';
export const useUploadFile = () => {
  const [fileName, setFileName] = useState('');
  const [isDraged, setIsDraged] = useState(false);
  const [isUrlUploader, setIsUrlUploader] = useState(false);

  const htmlEditorService = useHtmlEditorService();
  const { setIsOpen, setData } = useModalValidate();

  const { actionId } = useHtml();
  const { logger } = useHtmlEditorLogger();
  const { showWrongFileError, showInvalidFileError, showUploadFileMessage, showSaveMessage } =
    useAlertMessages();
  const { getHtmlByUrl, updateHtml } = useSaveHtml();

  const setIsLoading = useSetAtom(isLoadingUploadAtom);
  const setIsUploadFileOpen = useSetAtom(isUploadFilePageModalOpenAtom);
  const showValidateHTML = useFeatureFlag('showValidateHTML');

  const closeModal = () => setIsUploadFileOpen(false);

  const onDragEnter = () => {
    setIsDraged(true);
  };

  const onDragLeave = () => {
    setIsDraged(false);
  };

  const uploadFile = useCallback(
    async (actionId: number, htmlBody: string) => {
      const response = await updateHtml({
        html: htmlBody,
        actionId,
      });

      const error = response?.error ? true : false;

      const isSuccess = response && response.status === 200;

      if (!isSuccess) {
        showSaveMessage(false, response?.message);
        return;
      }

      if (!error) {
        await logger('uploadHtml', 'action', {
          actionId: actionId,
        });
      } else {
        await logger('uploadHtml', 'error', actionId);
      }

      showUploadFileMessage(isSuccess);
      isSuccess ? window.location.reload() : setIsLoading(false);
    },
    [updateHtml, showUploadFileMessage, setIsLoading, showSaveMessage, logger]
  );

  const getAndSaveHtmlByUrl = async (url: string, code: string) => {
    const result = await getHtmlByUrl({ url, actionId, code });
    const isSuccess = result && result.status === 200;
    showUploadFileMessage(isSuccess as boolean);
    isSuccess ? window.location.reload() : setIsLoading(false);
  };

  const onDrop = useCallback(
    async (acceptedFiles: File[], fileRejections: FileRejection[]) => {
      setIsDraged(false);
      if (fileRejections.length) {
        fileRejections.forEach(({ errors }: any) => {
          errors.forEach((error: any) => {
            if (error.code === 'file-invalid-type') {
              showWrongFileError();
            }
            if (error.code === 'file-too-large') {
              showInvalidFileError();
            }
          });
        });
      }

      for (const file of acceptedFiles) {
        const fileString = await file.text();

        setIsLoading(true);

        if (showValidateHTML) {
          const validateHtml = await htmlEditorService.validateHtml(fileString);

          setFileName(file.name);
          closeModal();
          if (validateHtml.errors.length > 0 || validateHtml.warnings.length > 0) {
            setIsOpen(true);
            setData(validateHtml);
            setIsLoading(false);
            return;
          }
        } else {
          setFileName(file.name);
          closeModal();
        }

        await uploadFile(actionId, fileString);
        setIsLoading(false);
      }
    },
    [
      showWrongFileError,
      showInvalidFileError,
      setIsLoading,
      htmlEditorService,
      closeModal,
      uploadFile,
      actionId,
      setIsOpen,
      setData,
      showValidateHTML,
    ]
  );

  const { getRootProps, open, getInputProps } = useDropzone({
    ...dropConfig,
    onDrop,
    onDragEnter,
    onDragLeave,
  });

  return {
    isUrlUploader,
    setIsUrlUploader,
    getRootProps,
    getInputProps,
    open,
    isDraged,
    fileName,
    getAndSaveHtmlByUrl,
  };
};
